import React, { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import CaseStudyEvidence from "./CaseStudyEvidence";
import {
  CASE_STUDY_BEFORE_AFTER,
  CASE_STUDY_CLIENT,
  CASE_STUDY_CLIENT_URL,
  CASE_STUDY_EVIDENCE_CAPTION,
  CASE_STUDY_EVIDENCE_LABEL,
  CASE_STUDY_FOOTNOTE,
  CASE_STUDY_HEADLINE_ACCENT,
  CASE_STUDY_HEADLINE_LINE2,
  CASE_STUDY_INTRO_PARTS,
  CASE_STUDY_LEAD,
  CASE_STUDY_METRICS,
  CASE_STUDY_OUTCOME,
  CASE_STUDY_SIDEBAR_META,
  CASE_STUDY_STAMP_LABEL,
  CASE_STUDY_STAMP_META,
} from "./caseStudyContent";

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const fadeUpVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const ClientLink = ({ children, className = "" }) => (
  <a
    href={CASE_STUDY_CLIENT_URL}
    target="_blank"
    rel="noreferrer"
    className={className}
  >
    {children}
  </a>
);

const CaseStudySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const reduceMotion = useReducedMotion();
  const animateMetrics = reduceMotion ? false : isInView;

  return (
    <section
      id="case-study"
      className="relative overflow-hidden py-16 sm:py-20 lg:py-24 border-y border-orange-500/15 bg-[#060a12]"
      data-testid="case-study-section"
      ref={ref}
    >
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(249,115,22,0.07)_0%,transparent_45%,rgba(20,184,166,0.05)_100%)] pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(-45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)",
          backgroundSize: "12px 12px",
        }}
      />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <div className="inline-flex items-center gap-3 mb-3">
              <span
                className="inline-block badge-mono text-[11px] sm:text-xs font-bold tracking-[0.35em] text-orange-950 bg-orange-400 px-4 py-2 rounded-sm shadow-lg shadow-orange-500/20 rotate-[-1deg]"
                data-testid="case-study-case-label"
              >
                {CASE_STUDY_STAMP_LABEL}
              </span>
              <span className="hidden sm:inline text-[10px] font-mono text-orange-500/50 tracking-widest">
                {CASE_STUDY_STAMP_META}
              </span>
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight text-white font-heading"
              data-testid="case-study-headline"
            >
              <span className="text-orange-400">{CASE_STUDY_HEADLINE_ACCENT}</span>
              <br />
              {CASE_STUDY_HEADLINE_LINE2}
            </h2>
            <p className="text-base sm:text-lg text-gray-400 font-body leading-relaxed mt-4 max-w-2xl">
              {CASE_STUDY_LEAD.beforeLink}
              <ClientLink className="text-white font-semibold hover:text-orange-400 transition-colors">
                {CASE_STUDY_CLIENT.name}
              </ClientLink>
              {CASE_STUDY_LEAD.afterLink}
            </p>
          </div>
          <p className="text-xs font-mono text-gray-500 sm:text-right max-w-[14rem] sm:pb-1">
            {CASE_STUDY_SIDEBAR_META}
          </p>
        </div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8 sm:mb-10"
          variants={staggerContainer}
          initial="hidden"
          animate={animateMetrics ? "visible" : "hidden"}
        >
          {CASE_STUDY_METRICS.map((metric, index) => (
            <motion.div
              key={metric.label}
              variants={reduceMotion ? undefined : fadeUpVariants}
              className={`relative rounded-xl p-5 sm:p-6 border ${
                metric.highlight
                  ? "border-orange-500/40 bg-gradient-to-br from-orange-500/15 via-orange-500/5 to-transparent ring-1 ring-orange-500/20"
                  : "border-white/[0.08] bg-white/[0.02]"
              }`}
              data-testid={index === 0 ? "case-study-stat" : undefined}
            >
              {metric.highlight && (
                <span className="absolute -top-2.5 left-4 badge-mono text-[9px] text-orange-400 bg-[#060a12] border border-orange-500/30 px-2 py-0.5 rounded-full">
                  KEY RESULT
                </span>
              )}
              <div
                className={`text-4xl sm:text-5xl font-bold font-mono mb-1 leading-none ${
                  metric.highlight ? "text-orange-400" : "text-teal-400"
                }`}
              >
                {metric.value}
              </div>
              <div className="text-sm font-semibold text-white mb-0.5">{metric.label}</div>
              <div className="text-[11px] font-mono text-gray-500">{metric.sub}</div>
            </motion.div>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          <div className="lg:col-span-5 space-y-5">
            <div className="flex items-start gap-4 p-5 rounded-xl border border-white/[0.08] bg-white/[0.02]">
              <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-orange-950 font-bold text-lg shadow-lg shadow-orange-500/20">
                {CASE_STUDY_CLIENT.initials}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-mono text-orange-400/80 tracking-widest mb-1">CLIENT</p>
                <p className="text-lg font-bold text-white leading-tight">
                  <ClientLink className="hover:text-orange-400 transition-colors">
                    {CASE_STUDY_CLIENT.name}
                  </ClientLink>
                </p>
                <p className="text-sm text-gray-500 font-body mt-1">{CASE_STUDY_CLIENT.region}</p>
              </div>
            </div>

            <p className="text-base text-gray-400 font-body leading-relaxed" data-testid="case-study-intro">
              {CASE_STUDY_INTRO_PARTS.beforeLink}
              <ClientLink className="text-white font-semibold hover:text-orange-400 transition-colors">
                {CASE_STUDY_INTRO_PARTS.linkText}
              </ClientLink>{" "}
              began booking{" "}
              <span className="text-orange-400 font-semibold">
                +6 additional qualified appointments per week
              </span>{" "}
              — captured in under{" "}
              <span className="text-teal-400 font-semibold">9 seconds</span>, including outside business
              hours, while the office workflow relied on a typical{" "}
              <span className="text-white font-semibold">30–40 minute</span> callback.
            </p>

            <div
              className="rounded-xl overflow-hidden border border-white/[0.08]"
              data-testid="case-study-thesis"
            >
              <div className="grid grid-cols-2 text-center text-[10px] sm:text-xs font-mono tracking-wider">
                <div className="py-3 px-2 bg-red-500/10 text-red-400/90 border-b border-r border-white/[0.06]">
                  <span className="block text-[9px] text-gray-600 mb-1">BEFORE</span>
                  <span className="line-through opacity-80">{CASE_STUDY_BEFORE_AFTER.before}</span>
                </div>
                <div className="py-3 px-2 bg-teal-500/10 text-teal-400 border-b border-white/[0.06]">
                  <span className="block text-[9px] text-gray-600 mb-1">AFTER</span>
                  <span className="font-semibold">{CASE_STUDY_BEFORE_AFTER.after}</span>
                </div>
              </div>
              <div className="py-3 px-4 text-center bg-orange-500/[0.08] border-t border-orange-500/20">
                <span className="text-xs sm:text-sm font-mono text-orange-400 font-semibold">
                  {CASE_STUDY_OUTCOME}
                </span>
              </div>
            </div>

            <p className="text-[11px] text-gray-600 font-mono" data-testid="case-study-disclaimer">
              {CASE_STUDY_FOOTNOTE}
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="relative p-3 sm:p-4 rounded-2xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 shadow-xl shadow-black/30">
              <div className="absolute -top-3 left-6 badge-mono text-[10px] text-gray-400 bg-[#060a12] border border-white/10 px-3 py-1 rounded-full">
                {CASE_STUDY_EVIDENCE_LABEL}
              </div>
              <CaseStudyEvidence className="rounded-md" />
              <p className="mt-3 text-center text-[10px] font-mono text-gray-600 tracking-wide">
                {CASE_STUDY_EVIDENCE_CAPTION}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudySection;
